import { useMemo, useState } from 'react';
import { AssociatedResourceProps } from 'componentsV2/product/details/content/subpages';
import { useAssociatedResource } from 'componentsV2/product/details/content/subpages/associated-resource/useAssociatedResource';
import { SInfiniteScroll } from '@setvi/shared/components';
import { ProductsEmptyState } from 'componentsV2/product/details/content/components';
import { Resource } from 'Interfaces';
import { Box, makeStyles } from '@material-ui/core';
import { buildResourceViewUrl } from '@setvi/shared/utils/viewer';
import { useProductAssetsEnabled } from 'hooksv2';
import {
  MobileResourceCard,
  MobileResourceLoader,
  MobileResourceOverlay
} from './components';
import { MobileProductAssets } from './product-assets';
import { TabContentWrapper } from '../styled';

const useStyles = makeStyles(({ spacing }) => ({
  wrapper: {
    marginTop: spacing(2)
  }
}));

const keyGetter = (resource: Resource) => resource?.ID || 'test';

/** Setvi associated resources: the Setvi API, previewed through the viewer iframe. */
const MobileSetviAssociatedResources = ({
  product
}: AssociatedResourceProps) => {
  const [selectedResource, setSelectedResource] = useState(null);
  const { resources, isLoading } = useAssociatedResource({ product });
  const classes = useStyles();

  const content = useMemo(() => {
    if (selectedResource)
      return (
        <MobileResourceOverlay
          title={selectedResource.Name}
          onBack={() => setSelectedResource(null)}>
          <iframe
            src={buildResourceViewUrl({
              name: selectedResource.Name,
              resourceTypeId: selectedResource.ResourceTypeID,
              resourceId: selectedResource.ResourceID
            })}
            style={{ width: '100%', height: '100%', border: 0 }}
            title="Resource Preview"
          />
        </MobileResourceOverlay>
      );

    if (isLoading) return <MobileResourceLoader />;

    if (!resources.length && !isLoading)
      return <ProductsEmptyState title="No Resources Available" />;

    return (
      <Box display="flex" flexDirection="column" gridGap={8}>
        <SInfiniteScroll
          list={resources}
          keyGetter={keyGetter}
          wrapperClass={classes.wrapper}
          onWrapperClick={setSelectedResource}
          render={resource => <MobileResourceCard resource={resource} />}
        />
        {isLoading && <MobileResourceLoader />}
      </Box>
    );
  }, [classes.wrapper, isLoading, resources, selectedResource]);

  return <TabContentWrapper>{content}</TabContentWrapper>;
};

/**
 * The mobile Resources tab. Product-assets companies (CFS, LD flag) read
 * revops-api product assets; everyone else keeps the Setvi associated
 * resources. Split so the disabled branch never fires its query.
 */
export const MobileAssociatedResources = (props: AssociatedResourceProps) => {
  const productAssetsEnabled = useProductAssetsEnabled();

  if (productAssetsEnabled)
    return <MobileProductAssets product={props.product} />;

  return <MobileSetviAssociatedResources {...props} />;
};
